import { useState, useMemo } from "react"
import { DateTime } from "luxon"
import {
  Info,
  MagnifyingGlass,
  X,
  FileX,
  IdentificationCard,
  ArrowBendUpRight,
  CaretDown,
} from "@phosphor-icons/react"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import {
  useOrderDeliverySummary,
  useMissingOrmInfinite,
  useUndeliveredInfinite,
  useReroutedInfinite,
} from "../../hooks/useOrderDeliveryInsights"
import useFetchPatientIssuers from "../../hooks/useFetchPatientIssuers"

const ORM_ISSUERS = [
  "USRS",
  "AKUMIN",
  "STAT IMAGING",
  "BEAR",
  "PROFESSIONAL IMAGING",
  "UPRIGHT MRI",
]

const LIMIT = 25

const FILTER_LABEL =
  "text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1"

const SummaryCard = ({ label, value, sub, accent, icon: Icon, iconCls }) => (
  <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm p-4 overflow-hidden">
    <span className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      {Icon && (
        <span className={`p-2 rounded-full ${iconCls}`}>
          <Icon className="h-5 w-5" weight="bold" />
        </span>
      )}
    </div>
    <p className="text-3xl font-extrabold text-primary mt-2 leading-none tabular-nums">
      {value}
    </p>
    {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
  </div>
)

const EmptyRow = ({ colSpan, children }) => (
  <tr>
    <td className="py-10" colSpan={colSpan}>
      <div className="flex items-center justify-center text-gray-500">
        <Info className="h-5 w-5 mr-2 text-gray-400" weight="bold" />
        {children}
      </div>
    </td>
  </tr>
)

const OrderDeliveryInsights = () => {
  const today = DateTime.now().toFormat("yyyy-MM-dd")
  const weekAgo = DateTime.now().minus({ days: 6 }).toFormat("yyyy-MM-dd")

  const [dateFrom, setDateFrom] = useState(weekAgo)
  const [dateTo, setDateTo] = useState(today)
  const [issuer, setIssuer] = useState("")
  const [repush, setRepush] = useState("")
  const [tab, setTab] = useState("missing")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")

  const baseFilters = useMemo(
    () => ({ dateFrom, dateTo, issuer: issuer || undefined }),
    [dateFrom, dateTo, issuer]
  )

  const { data: summary } = useOrderDeliverySummary({ filters: baseFilters })

  const missing = useMissingOrmInfinite({
    limit: LIMIT,
    filters: {
      ...baseFilters,
      search: search || undefined,
      needsRepush: repush || undefined,
    },
  })

  const mismatch = useUndeliveredInfinite({
    limit: LIMIT,
    filters: {
      ...baseFilters,
      reason: "DATA_MISMATCH",
      search: search || undefined,
    },
  })

  const rerouted = useReroutedInfinite({
    limit: LIMIT,
    filters: { ...baseFilters, search: search || undefined },
  })

  const active =
    tab === "missing" ? missing : tab === "mismatch" ? mismatch : rerouted
  const rows = active.data?.pages.flatMap((p) => p.data) ?? []
  const total = active.data?.pages?.[0]?.total ?? 0
  const searchedIssuers = search
    ? [...new Set(rows.map((r) => r.PatientIssuer).filter(Boolean))]
    : []

  const { data: issuerData } = useFetchPatientIssuers()
  const reroutedIssuerOptions = useMemo(
    () =>
      Array.from(
        new Set([...ORM_ISSUERS, ...(issuerData?.clients || [])])
      ).sort((a, b) => String(a).localeCompare(String(b))),
    [issuerData]
  )
  const issuerOptions = tab === "rerouted" ? reroutedIssuerOptions : ORM_ISSUERS

  const changeTab = (next) => {
    setTab(next)
    const nextOptions =
      next === "rerouted" ? reroutedIssuerOptions : ORM_ISSUERS
    if (issuer && !nextOptions.includes(issuer)) setIssuer("")
  }

  const applySearch = () => setSearch(searchInput.trim())
  const clearSearch = () => {
    setSearchInput("")
    setSearch("")
  }

  const resetFilters = () => {
    setDateFrom(weekAgo)
    setDateTo(today)
    setIssuer("")
    setRepush("")
    clearSearch()
  }

  const onScroll = (e) => {
    const el = e.currentTarget
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 120 &&
      active.hasNextPage &&
      !active.isFetchingNextPage
    ) {
      active.fetchNextPage()
    }
  }

  if (active.isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={active.error} />
      </div>
    )
  }

  return (
    <div className="max-w-[1240px] mx-auto px-4 py-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        <SummaryCard
          accent="bg-red-500"
          icon={FileX}
          iconCls="bg-red-50 text-red-600"
          label="Missing ORM"
          value={summary?.missingOrm ?? "—"}
        />
        <SummaryCard
          accent="bg-amber-500"
          icon={IdentificationCard}
          iconCls="bg-amber-50 text-amber-600"
          label="Patient DOB or MRN Mismatched"
          value={summary?.dataMismatch ?? "—"}
        />
        <SummaryCard
          accent="bg-violet-500"
          icon={ArrowBendUpRight}
          iconCls="bg-violet-50 text-violet-600"
          label="Rerouted"
          value={summary?.rerouted ?? "—"}
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 mt-5">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex flex-col">
            <span className={FILTER_LABEL}>Date Range</span>
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-1.5">
              <input
                className="text-sm outline-none"
                max={dateTo || today}
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span className="text-gray-400">–</span>
              <input
                className="text-sm outline-none"
                max={today}
                min={dateFrom || undefined}
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col">
            <span className={FILTER_LABEL}>Issuer</span>
            <div className="relative min-w-[220px]">
              <select
                className="appearance-none w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-9 text-sm text-gray-900 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary hover:cursor-pointer"
                value={issuer}
                onChange={(e) => setIssuer(e.target.value)}
              >
                <option value="">All issuers</option>
                {issuerOptions.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
              <CaretDown className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-3 h-3.5 w-3.5 text-gray-400" />
            </div>
          </div>

          {tab === "missing" && (
            <div className="flex flex-col">
              <span className={FILTER_LABEL}>Order arrived late</span>
              <div className="relative min-w-[180px]">
                <select
                  className="appearance-none w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-9 text-sm text-gray-900 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary hover:cursor-pointer"
                  value={repush}
                  onChange={(e) => setRepush(e.target.value)}
                >
                  <option value="">All studies</option>
                  <option value="true">Needs re-push</option>
                </select>
                <CaretDown className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-3 h-3.5 w-3.5 text-gray-400" />
              </div>
            </div>
          )}

          <div className="flex flex-col">
            <span className={FILTER_LABEL}>Accession Number</span>
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-1.5 min-w-[250px]">
              <MagnifyingGlass className="h-4 w-4 text-gray-400" />
              <input
                className="text-sm outline-none w-full"
                placeholder="Search and press Enter"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
              />
              {searchInput && (
                <button
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" weight="bold" />
                </button>
              )}
            </div>
            {search && searchedIssuers.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mt-1.5">
                <span className="text-[11px] text-gray-400 self-center">
                  Issuer:
                </span>
                {searchedIssuers.map((iss) => (
                  <span
                    key={iss}
                    className="text-xs font-semibold rounded-full px-2.5 py-0.5 bg-sky-100 text-secondary_dark"
                  >
                    {iss}
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            className="text-sm font-semibold text-gray-500 border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50"
            type="button"
            onClick={resetFilters}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mt-5 overflow-hidden">
        <div className="flex gap-1 px-4 pt-3 border-b border-gray-200">
          {[
            {
              key: "missing",
              label: "Missing ORM",
              count: summary?.missingOrm,
            },
            {
              key: "mismatch",
              label: "Patient DOB or MRN Mismatched",
              count: summary?.dataMismatch,
            },
            {
              key: "rerouted",
              label: "Rerouted",
              count: summary?.rerouted,
            },
          ].map((t) => (
            <button
              key={t.key}
              className={`flex items-center gap-2 px-4 pb-3 pt-2 text-sm font-semibold border-b-[3px] transition-colors ${
                tab === t.key
                  ? "text-primary border-secondary"
                  : "text-gray-500 border-transparent"
              }`}
              onClick={() => changeTab(t.key)}
            >
              {t.label}
              {Number.isFinite(t.count) && (
                <span
                  className={`text-xs font-bold rounded-full px-2 py-0.5 tabular-nums ${
                    tab === t.key
                      ? "bg-sky-100 text-secondary_dark"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-auto max-h-[60vh]" onScroll={onScroll}>
          <table className="w-full border-collapse min-w-[720px]">
            <thead className="sticky top-0 z-10 bg-primary">
              <tr className="bg-primary text-white text-left text-xs">
                <th className="py-3 px-4 font-bold">Accession No</th>
                <th className="py-3 px-4 font-bold">Patient Issuer</th>
                <th className="py-3 px-4 font-bold">Result Received On</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {active.isLoading ? (
                <tr>
                  <td className="py-12" colSpan={3}>
                    <div className="flex justify-center">
                      <Loader className="h-6 w-6" />
                    </div>
                  </td>
                </tr>
              ) : !rows.length ? (
                <EmptyRow colSpan={3}>No studies found for this range</EmptyRow>
              ) : (
                rows.map((row) => (
                  <tr
                    key={`${row.PatientIssuer}_${row.AccessionNo}`}
                    className="transition-colors"
                  >
                    <td className="py-3 px-4 font-bold text-secondary_dark tabular-nums">
                      {row.AccessionNo}
                    </td>
                    <td className="py-3 px-4 text-sm">{row.PatientIssuer}</td>
                    <td className="py-3 px-4 text-sm text-gray-500 tabular-nums">
                      {row.ReceivedAt
                        ? DateTime.fromISO(row.ReceivedAt).toFormat(
                            "MM/dd/yyyy HH:mm"
                          )
                        : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {active.isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Loader className="h-5 w-5" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
          <span className="tabular-nums">
            Showing {rows.length} of {total} {total === 1 ? "study" : "studies"}
          </span>
          {active.hasNextPage && (
            <span className="text-gray-400">Scroll for more</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderDeliveryInsights
